import axios from "axios";

/** The finance guide routes all CFS requests through the fair_merchant gateway. */
export const resolveFinanceBase = (env = process.env) =>
  (
    env.REACT_APP_FINANCE_API_BASE_URL ||
    env.REACT_APP_API_BASE_URL ||
    "https://merchantapex.fairsurepay.com"
  ).replace(/\/+$/, "");
export const FINANCE_BASE = resolveFinanceBase();

export const PAYOUT_BASE = (
  process.env.REACT_APP_PAYOUT_API_BASE_URL ||
  "https://merchantapex.fairsurepay.com"
).replace(/\/$/, "");

/** Admin merchant overrides (IP allowlists) live under their own gateway prefix. */
export const resolveAdminMerchantBase = (env = process.env) =>
  (
    env.REACT_APP_ADMIN_MERCHANT_API_BASE_URL ||
    env.REACT_APP_API_BASE_URL ||
    "https://merchantapex.fairsurepay.com"
  ).replace(/\/+$/, "");
export const ADMIN_MERCHANT_BASE = resolveAdminMerchantBase();

/** Guide envelopes are unwrapped once; HTTP and application failures both reject. */
export async function adminRequest(
  base,
  path,
  { token, signal, method = "GET", data, params } = {}
) {
  try {
    const response = await axios({
      url: `${base}${path}`,
      method,
      data,
      params,
      signal,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
    const envelope = response.data;
    if (
      envelope?.success === false ||
      (typeof envelope?.status === "number" && envelope.status >= 400) ||
      envelope?.status === "error"
    ) {
      const failure = new Error(
        envelope.message || envelope.errorMessage || "Request failed"
      );
      failure.status =
        typeof envelope.status === "number" ? envelope.status : 400;
      throw failure;
    }
    return envelope.data;
  } catch (error) {
    if (axios.isCancel(error) || error.name === "AbortError") throw error;
    const status = error.response?.status || error.status;
    const payload = error.response?.data;
    const message =
      status === 401
        ? "Your session has expired. Please sign in again."
        : status === 403
        ? "Access denied. Your account may be suspended; please contact support."
        : !status || status >= 500
        ? "Unable to reach the service. Please try again."
        : payload?.message ||
          payload?.errorMessage ||
          payload?.detail ||
          error.message ||
          "Request failed";
    const failure = new Error(message);
    failure.status = status;
    throw failure;
  }
}
export const financeApi = (path, options) =>
  adminRequest(FINANCE_BASE, `/api/admin/finance${path}`, options);
export const payoutApi = (path, options) =>
  adminRequest(PAYOUT_BASE, `/api/admin/payouts${path}`, options);
/** Admin merchant operations such as the IP allowlist override. */
export const merchantAdminApi = (path, options) =>
  adminRequest(ADMIN_MERCHANT_BASE, `/api/admin/merchant/merchant/${path}`, options);
export const validRejection = (reason) => reason.trim().length >= 5;
export const settlementParams = (page, size, status) => ({
  page,
  size,
  ...(status ? { status } : {}),
});

/** Global sub-user directory filters documented for the admin finance gateway. */
export const subUserParams = (page, size, filters = {}) => ({
  page,
  size,
  ...(filters.search ? { search: filters.search } : {}),
  ...(filters.status ? { status: filters.status } : {}),
  ...(filters.merchantId ? { merchantId: filters.merchantId } : {}),
});

/** Platform wallet directory filters; only documented query keys are sent. */
export const walletParams = (page, size, walletType) => ({
  page,
  size,
  ...(walletType ? { walletType } : {}),
});

/** Sub-user directory rows use a `subUsers` key; older builds used `content`. */
export const subUserRows = (payload) =>
  Array.isArray(payload) ? payload : payload?.subUsers || payload?.content || [];

/** Wallet directory rows use a `wallets` key; older builds used `content`. */
export const walletRows = (payload) =>
  Array.isArray(payload) ? payload : payload?.wallets || payload?.content || [];

/** Paged payloads report totals as `totalCount` or `totalElements`. */
export const totalCount = (payload) =>
  payload?.totalCount ?? payload?.totalElements ?? null;

/** Allowlist payloads keep the raw list plus the cooldown counters. */
export const allowedIpList = (payload) => {
  if (Array.isArray(payload)) return payload;
  return payload?.allowedIps || [];
};

/** Supports the live array response and the earlier paginated batch contract. */
export const settlementRows = (payload) =>
  (Array.isArray(payload) ? payload : payload?.content || [])
    .filter((row) => row && (row.batchReference || row.batchId != null))
    .map((row) => ({
      ...row,
      batchReference: row.batchReference || String(row.batchId),
      merchantId: row.externalMerchantId ?? row.merchantId,
      batchTotal: row.pendingGrossVolume ?? row.batchTotal,
      accountNumber: row.holdingAccountNumber ?? row.accountNumber,
      createdAt: row.calculatedAt ?? row.createdAt,
    }));
